import React, { useEffect, useState, useMemo, useCallback } from "react";
import { AiFillHeart, AiOutlineShoppingCart } from "react-icons/ai";
import { FaEye, FaWhatsapp } from "react-icons/fa";
import { motion, useAnimation } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Ratings from "../Ratings";
import {
  add_to_card,
  messageClear,
  add_to_wishlist,
} from "../../store/reducers/cardReducer";

const ProductCard = React.memo(
  ({
    product,
    index,
    hoveredCard,
    onMouseEnter,
    onMouseLeave,
    addCard,
    addWishlist,
  }) => {
    const navigate = useNavigate();
    const controls = useAnimation();

    useEffect(() => {
      controls.start({
        opacity: hoveredCard === index ? 1 : 0.9,
        scale: hoveredCard === index ? 1.02 : 1,
        transition: { duration: 0.3 },
      });
    }, [controls, hoveredCard, index]);

    return (
      <motion.div
        key={index}
        className="hover:border hover:border-secondary/20 group transition-all duration-300 hover:bg-card rounded-3xl hover:shadow-sm hover:-mt-1 cursor-pointer"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={() => {
          if (window.innerWidth < 768) return; // mobile
          navigate(`/product/details/${product.slug}`);
        }}
        initial={{ opacity: 0.9, scale: 1 }}
        animate={controls}
      >
        <div className="relative transition-opacity duration-500 ease-in-out overflow-hidden">
          {product.discount ? (
            <div className="flex justify-center items-center absolute text-white w-[38px] h-[38px] rounded-full bg-red-500 font-semibold text-xs left-2 top-2">
              -{product.discount}%
            </div>
          ) : (
            ""
          )}
          <img
            className="sm:w-full w-full rounded-3xl group-hover:rounded-b-none transition-all duration-200 h-[240px] object-cover"
            src={(hoveredCard === index && product.images[1]) ? product.images[1] : product.images[0]}
            alt="product"
          />
          <ul className="flex transition-all duration-500 -bottom-11 justify-center items-center gap-2 absolute w-full group-hover:bottom-3">
            <li
              onClick={(e) => {
                e.stopPropagation();
                addWishlist(product._id);
              }}
              className="w-[44px] h-[44px] cursor-pointer bg-card text-secondary flex justify-center items-center rounded-full hover:bg-secondary hover:text-card hover:rotate-[720deg] transition-all"
            >
              <AiFillHeart className="text-xl" />
            </li>
            <Link
              onClick={(e) => e.stopPropagation()}
              to={`/product/details/${product.slug}`}
              className="w-[44px] h-[44px] cursor-pointer bg-card text-secondary flex justify-center items-center rounded-full hover:bg-secondary hover:text-card hover:rotate-[720deg] transition-all"
            >
              <FaEye className="text-xl" />
            </Link>
            <li
              onClick={(e) => {
                e.stopPropagation();
                addCard(product._id);
              }}
              className="w-[44px] h-[44px] cursor-pointer bg-card text-secondary flex justify-center items-center rounded-full hover:bg-secondary hover:text-card hover:rotate-[720deg] transition-all"
            >
              <AiOutlineShoppingCart className="text-xl" />
            </li>
            {product.sellerId?.whatsappNumber && (
              <a
                onClick={(e) => e.stopPropagation()}
                href={`https://wa.me/${product.sellerId.whatsappNumber}?text=${encodeURIComponent(
                  `Hello, I want to buy this product:\n\n*Name:* ${product.name}\n*SKU:* ${product.sku || 'N/A'}\n*Price:* Rs.${product.discount > 0 ? product.price - Math.floor((product.price * product.discount) / 100) : product.price}\n*Link:* ${window.location.origin}/product/details/${product.slug}`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-[44px] h-[44px] cursor-pointer bg-card text-green-500 flex justify-center items-center rounded-full hover:bg-green-500 hover:text-white hover:rotate-[720deg] transition-all"
              >
                <FaWhatsapp className="text-xl" />
              </a>
            )}
          </ul>
        </div>
        <div className="py-3 text-primary px-2">
          <h2 className="text-lg font-medium">{product.name}</h2>
          <div className="flex justify-start items-center gap-3 text-xl mt-1">
            {product.discount !== 0 ? (
              <>
                <p className="line-through">Rs.{product.price}</p>
                <p className="font-medium">
                  Rs.
                  {product.price -
                    Math.floor((product.price * product.discount) / 100)}{" "}
                </p>
              </>
            ) : (
              <p className="font-medium">Rs.{product.price}</p>
            )}
            <div className="flex">
              <Ratings ratings={product.rating} />
            </div>
          </div>
        </div>
      </motion.div>
    );
  },
);

const FeatureProducts = ({ products }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.auth);
  const { successMessage, errorMessage } = useSelector((state) => state.card);

  const [hoveredCard, setHoveredCard] = useState(null);

  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      dispatch(messageClear());
    }
    if (errorMessage) {
      toast.error(errorMessage);
      dispatch(messageClear());
    }
  }, [errorMessage, dispatch, successMessage]);

  const add_card = useCallback(
    (id) => {
      if (userInfo) {
        dispatch(
          add_to_card({
            userId: userInfo.id,
            quantity: 1,
            productId: id,
          }),
        );
      } else {
        navigate("/login");
      }
    },
    [dispatch, navigate, userInfo],
  );

  const memoizedProductCards = useMemo(() => {
    const add_wishlist = (pro) => {
      if (userInfo) {
        dispatch(
          add_to_wishlist({
            userId: userInfo.id,
            productId: pro._id,
            name: pro.name,
            price: pro.price,
            image: pro.images[0],
            discount: pro.discount,
            rating: pro.rating,
            slug: pro.slug,
          }),
        );
      } else {
        navigate("/login");
      }
    };

    return products.map((p, i) => (
      <ProductCard
        key={i}
        product={p}
        index={i}
        hoveredCard={hoveredCard}
        onMouseEnter={() => setHoveredCard(i)}
        onMouseLeave={() => setHoveredCard(null)}
        addCard={() => add_card(p._id)}
        addWishlist={() => add_wishlist(p)}
      />
    ));
  }, [products, hoveredCard, add_card, dispatch, navigate, userInfo]);

  return (
    <div className="w-[90%] flex flex-wrap mx-auto">
      <div className="w-full">
        <div className="text-center flex justify-center items-center flex-col text-4xl sm:text-3xl text-primary font-bold relative pb-[45px]">
          <h2>Featured Products</h2>
          <div className="w-[200px] h-[4px] bg-secondary mt-4"></div>
        </div>
      </div>
      <div className="w-full  grid grid-cols-4 md-lg:grid-cols-3 md:grid-cols-2  sm:grid-cols-1 gap-6">
        {memoizedProductCards}
      </div>
    </div>
  );
};

export default FeatureProducts;
